'use client'

import { ReactNode, useEffect, useState } from 'react';
import Link from 'next/link';
import styled from 'styled-components';

import { useMediaQuery, usePrevious } from '@/utils/hooks';
import { theme } from '@/assets/styles/themeConfig';
interface Props {
  mode: "dark" | "light";
  sticky?: boolean;
  className?: string;
  overlay?: boolean;
  scrollAway?: boolean;
  children?: ReactNode;
  social?: boolean;
  noblur?: boolean;
  noscrolling?: boolean;
}

const TopBarMinimal = ({
  mode = "light",
  noblur = false,
  noscrolling = false,
  sticky,
  className = "",
  scrollAway = false,
  overlay,
  social = true,
  children
}: Props) => {
  const isMobileUp = useMediaQuery(`${theme.mediaQuery.phone}`);
  const [ scrollPos, setScrollPos ] = useState<number>(0);
  const prevScrollPos = usePrevious(scrollPos);

  const svgColor = (mode: string) => {
    switch(mode) {
      case 'dark': 
        return '#ffffff';
      case 'light':
        return '#000000';
      default:
        return '#000000';
    }
  }

  const handleScroll = () => {
    setScrollPos(window.pageYOffset);
  };

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    }
  }, [])

  return (
    <TopBar
      className={`
        ${className}
        ${scrollPos > 0 && !noscrolling && "scrolling"}
        ${scrollAway && prevScrollPos < scrollPos ? "hidden" : ""}
      `}
      sticky={sticky}
      overlay={overlay}
      mode={mode}
      noblur={noblur}
    >
      <div className="inner-wrapper">
        <div className="logo">
          <Link href="/" aria-label="Plaen Logo">
            { isMobileUp ? (
              <svg viewBox="12.468 13.007 99.48 16.884" width="99.48" height="16.884">
                <g className="wordmark" transform="matrix(1, 0, 0, 1, -33.071514, 3.030447)" fill={svgColor(mode)}>
                  <path d="M205.28 169.59a6.79 6.79 0 0 0-3.32-.74h-6.27v17.08h3.17v-5.75h3.08a6.86 6.86 0 0 0 3.33-.74 5 5 0 0 0 2-2 6.1 6.1 0 0 0 .68-2.89 6.22 6.22 0 0 0-.67-2.89 5 5 0 0 0-2-2.07Zm-6.42 2h2.76a4 4 0 0 1 1.84.37 2.37 2.37 0 0 1 1 1 3.47 3.47 0 0 1 .32 1.52 3.55 3.55 0 0 1-.32 1.53 2.37 2.37 0 0 1-1 1 3.82 3.82 0 0 1-1.82.38h-2.79Z" transform="translate(-150.15 -158.87)"></path>
                  <polygon points="70.16 9.98 66.98 9.98 66.98 27.06 77.6 27.06 77.6 24.29 70.16 24.29 70.16 9.98"></polygon>
                  <path d="m242.42 168.85-6.11 17.08h3.37l1.47-4.29h6l1.47 4.29H252l-6.11-17.08Zm-.35 10.09 2.1-6.12 2.09 6.12Z" transform="translate(-150.15 -158.87)"></path>
                  <polygon points="113.61 19.9 120.92 19.9 120.92 17.12 113.61 17.12 113.61 12.75 121.46 12.75 121.46 9.98 110.43 9.98 110.43 27.06 121.54 27.06 121.54 24.29 113.61 24.29 113.61 19.9"></polygon>
                  <polygon points="141.88 9.98 141.88 21.54 133.96 10.09 133.88 9.98 131.06 9.98 131.06 27.06 134.23 27.06 134.23 15.52 142.22 27.06 145.02 27.06 145.02 9.98 141.88 9.98"></polygon>
                </g>
              </svg>
            ) : (
              <svg width="343" height="193" viewBox="0 0 343 193" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M323.841 14.208L127.431 0.0679769C123.45 -0.22176 119.456 0.417338 115.764 1.93489C112.072 3.45244 108.783 5.80696 106.156 8.81276C103.529 11.8186 101.637 15.3934 100.628 19.2556C99.6188 23.1177 99.5207 27.1615 100.341 31.068L129.051 168.398C129.213 169.173 129.133 169.98 128.823 170.708C128.513 171.437 127.988 172.054 127.317 172.475C126.646 172.896 125.863 173.102 125.072 173.065C124.281 173.028 123.52 172.75 122.891 172.268L73.6714 134.458L22.1714 94.898C21.2388 94.1722 20.5308 93.1972 20.1291 92.0859C19.7274 90.9746 19.6484 89.7723 19.9013 88.618C20.1859 87.3544 20.8604 86.2121 21.8293 85.3526C22.7983 84.4931 24.0129 83.9598 25.3014 83.828L73.6714 78.6879L93.6114 76.5679L89.5514 57.138L73.6714 58.828L23.2113 64.188C18.0541 64.7335 13.1787 66.8123 9.21464 70.1561C5.25056 73.4999 2.37975 77.9552 0.972821 82.9467C-0.434111 87.9383 -0.312555 93.2369 1.32176 98.1587C2.95607 103.08 6.02812 107.399 10.1413 110.558L73.6714 159.358L110.871 187.928C114.862 191.01 119.737 192.729 124.779 192.83C129.82 192.93 134.761 191.408 138.871 188.488L333.971 50.748C337.364 48.3527 339.933 44.9673 341.326 41.0554C342.72 37.1435 342.871 32.8964 341.757 28.8957C340.644 24.895 338.321 21.3364 335.106 18.707C331.892 16.0777 327.943 14.5063 323.801 14.208H323.841Z" fill={svgColor(mode)}/>
              </svg>
            )}
          </Link>
        </div>
        {social ? (
          <div className="social-header">
            <Link href="https://www.instagram.com/plaenco/" target="_blank" aria-label="Instagram">
              <i aria-hidden className="fab fa-instagram" />
            </Link>
            <Link href="https://www.tiktok.com/@plaenco" target="_blank" aria-label="TikTok">
              <i aria-hidden className="fab fa-tiktok" />
            </Link>
          </div>
        ): null}
        <ActionButtons>{children}</ActionButtons>
      </div>
    </TopBar>
  )
}

export const TOPBAR_HEIGHT = "4.25rem";
export const EXPANDED_TOPBAR_HEIGHT = "4.25rem";

export const MOBILE_TOPBAR_HEIGHT = "3.75rem";
export const MOBILE_EXPANDED_TOPBAR_HEIGHT = "3.75rem";

const TopBar = styled.div<Pick<Props, 'sticky' | 'overlay' | 'mode' | 'noblur'>>`
  height: ${MOBILE_EXPANDED_TOPBAR_HEIGHT};
  position: relative;
  z-index: 1000;
  padding: 0 1rem;
  transition: all 0.3s cubic-bezier(0.77,0,0.175,1);
  ${({ noblur }) => noblur ? '' : 'backdrop-filter: saturate(180%) blur(1.5rem);'}
  border-bottom: transparent;
  background-color: rgba(255,255,255,0);

  @media ${({ theme }) => theme.mediaQuery.smallTablet} {
    height: ${EXPANDED_TOPBAR_HEIGHT};
  }
  
  &.hidden {
    /* transform: translateY(-100%); */
  }

  // when not at the top
  &.scrolling {
    background-color: ${({ mode }) => mode === "dark" ? "rgba(255,255,255,0.1)" : "rgba(255,255,255,0.66)"};
    /* border-bottom: 1px solid rgba(0, 0, 0, 0.1); */
    height: ${MOBILE_TOPBAR_HEIGHT};

    @media ${({ theme }) => theme.mediaQuery.smallTablet} {
      height: ${TOPBAR_HEIGHT};
    }
  

    ${({ overlay, theme }) => overlay && `
      margin-bottom: -${MOBILE_TOPBAR_HEIGHT};

      @media ${theme.mediaQuery.smallTablet} {
        margin-bottom: ${TOPBAR_HEIGHT};
      }
    `}
  
    .inner-wrapper {
      height: ${MOBILE_TOPBAR_HEIGHT};

      @media ${({ theme }) => theme.mediaQuery.smallTablet} {
        height: ${TOPBAR_HEIGHT};
      }
    }
  }

  ${({ sticky }) => sticky && `
    position: sticky;
    top: 0;
  `}

  ${({ overlay, theme }) => overlay && `
    margin-bottom: -${MOBILE_EXPANDED_TOPBAR_HEIGHT};

    @media ${theme.mediaQuery.smallTablet} {
      margin-bottom: ${EXPANDED_TOPBAR_HEIGHT};
    }
  `}
  
  .inner-wrapper {
    transition: all 0.3s cubic-bezier(0.77,0,0.175,1);
    /* max-width: 90rem; */
    width: 100%;
    display: flex;
    align-items: center;
    height: ${MOBILE_EXPANDED_TOPBAR_HEIGHT};

    @media ${({ theme }) => theme.mediaQuery.smallTablet} {
      height: ${EXPANDED_TOPBAR_HEIGHT};
      width: calc(100% - 2rem);
      margin: 0 0 0 1rem;
    }

    .logo {
      flex: 1 1 auto;
      display: flex;
      align-items: center;
      justify-content: flex-start;

      svg {
        position: relative;
        top: 0.25rem;
        height: 1.5rem;
        width: auto;

        @media ${({ theme }) => theme.mediaQuery.phone} {
          height: 1.2rem;
          top: 0.1rem;
        }
      }
    }

    .social-header {
      display: flex;
      align-items: center;
      
      a {
        padding: 0.2rem;
        margin: 0 0.2rem;
        font-size: 1.3rem;
        color: ${({ mode }) => mode === "dark" ? "#ffffff" : "#000000"};
        opacity: ${({ mode }) => mode === "dark" ? 0.4 : 0.6};

        &:hover {
          opacity: 1;
        }
      }
    }
  }
`

const ActionButtons = styled.div`
  > * { 
    margin-left: 0.5rem;
    margin-top: 0;

    @media ${({ theme }) => theme.mediaQuery.smallTablet} {
      margin-left: 1rem;
    }
  }
`

export default TopBarMinimal;
